import type { ComponentType, ReactElement } from 'react';
import { matchPath, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AppShell } from '@/app/layout/AppShell';
import { allNavItems, type NavItem } from '@/app/navigation';
import type { RotaAvulsa, RotasDoModulo } from '@/app/rotas';
import { rotas as rotasDeAftercare } from '@/features/aftercare/rotas';
import { BrokerPublicSignupPage } from '@/features/auth/BrokerPublicSignupPage';
import { rotas as rotasDeAuth } from '@/features/auth/rotas';
import { rotas as rotasDeBrokers } from '@/features/brokers/rotas';
import { rotas as rotasDeCounterparties } from '@/features/counterparties/rotas';
import { rotas as rotasDeCreditConsumption } from '@/features/creditConsumption/rotas';
import { rotas as rotasDeCreditSettings } from '@/features/creditSettings/rotas';
import { rotas as rotasDeCreditSimulator } from '@/features/creditSimulator/rotas';
import { rotas as rotasDeDashboard } from '@/features/dashboard/rotas';
import { rotas as rotasDeDemands } from '@/features/demands/rotas';
import { rotas as rotasDeDocuments } from '@/features/documents/rotas';
import { rotas as rotasDeGeneration } from '@/features/generation/rotas';
import { rotas as rotasDeInsureds } from '@/features/insureds/rotas';
import { rotas as rotasDeIntegrations } from '@/features/integrations/rotas';
import { rotas as rotasDeLogs } from '@/features/logs/rotas';
import { rotas as rotasDeOperations } from '@/features/operations/rotas';
import { rotas as rotasDeParametrization } from '@/features/parametrization/rotas';
import { rotas as rotasDePipes } from '@/features/pipes/rotas';
import { ModulePlaceholder } from '@/features/placeholder/ModulePlaceholder';
import { rotas as rotasDePlatform } from '@/features/platform/rotas';
import { rotas as rotasDePolicyholders } from '@/features/policyholders/rotas';
import { rotas as rotasDePortfolio } from '@/features/portfolio/rotas';
import { rotas as rotasDePricing } from '@/features/pricing/rotas';
import { rotas as rotasDeProducts } from '@/features/products/rotas';
import { rotas as rotasDeReports } from '@/features/reports/rotas';
import { rotas as rotasDeSettings } from '@/features/settings/rotas';
import { rotas as rotasDeStatus } from '@/features/status/rotas';
import { NotFoundPage, PublicNotFoundPage } from '@/features/status/StatusPages';
import { rotas as rotasDeTemplates } from '@/features/templates/rotas';
import { rotas as rotasDeTerms } from '@/features/terms/rotas';
import { rotas as rotasDeUsers } from '@/features/users/rotas';
import { useAuth } from '@/shared/auth/useAuth';
import { Spinner } from '@/shared/ui/Spinner';

/**
 * Modulos que registram rotas, cada um em `features/<modulo>/rotas.tsx`.
 *
 * Modulo novo entra com uma linha de importacao e uma linha aqui. A ordem desta lista
 * e a das rotas fora do menu; as telas do menu seguem a ordem de `app/navigation.ts`.
 * Quem escolhe a rota que atende um endereco e o React Router, pela mais especifica:
 * a ordem so desempata rotas que casam o mesmo endereco, e o teste do router recusa
 * rota coberta por outra.
 */
const modulos: RotasDoModulo[] = [
  rotasDeAuth,
  rotasDeDashboard,
  rotasDeReports,
  rotasDeOperations,
  rotasDeDemands,
  rotasDePipes,
  rotasDeBrokers,
  rotasDePolicyholders,
  rotasDeInsureds,
  rotasDeGeneration,
  rotasDePortfolio,
  rotasDeAftercare,
  rotasDePricing,
  rotasDeCounterparties,
  rotasDeProducts,
  rotasDeParametrization,
  rotasDeDocuments,
  rotasDeTemplates,
  rotasDeUsers,
  rotasDeTerms,
  rotasDeIntegrations,
  rotasDeLogs,
  rotasDeSettings,
  rotasDeCreditSimulator,
  // Configuracao da analise de credito (C-ADM).
  rotasDeCreditSettings,
  rotasDeStatus,
  rotasDePlatform,
  rotasDeCreditConsumption,
];

/**
 * Modulos ja implementados, pelo caminho do item do menu.
 *
 * O que nao esta aqui continua com o marcador de roteiro, que descreve o estado
 * real em vez de abrir uma tela vazia que aparenta estar quebrada.
 */
const telasDoMenu: Record<string, ComponentType> = Object.fromEntries(
  modulos.flatMap((modulo) => Object.entries(modulo.menu ?? {})),
);

/** Rotas da moldura da empresa que nao sao item do menu. */
const rotasDaMoldura = modulos.flatMap((modulo) => modulo.moldura ?? []);

/** Rotas fora da moldura da empresa. */
const rotasAvulsas = modulos.flatMap((modulo) => modulo.avulsas ?? []);

function FullPageLoader() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <Spinner size={28} label="Carregando" className="text-brand-500" />
    </div>
  );
}

/**
 * Exige sessao valida e preserva o destino pretendido para depois do acesso.
 *
 * Sem sessao, o endereco que nao existe recebe a pagina nao encontrada, e nao a tela
 * de acesso: mandar entrar para depois dizer que a pagina nao existe e o caminho
 * mais longo para a mesma resposta.
 */
function RequireAuth({
  children,
  conhecida,
}: {
  children: ReactElement;
  conhecida?: (caminho: string) => boolean;
}) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <FullPageLoader />;
  if (status === 'anonymous') {
    if (conhecida && !conhecida(location.pathname)) {
      return <PublicNotFoundPage />;
    }
    return <Navigate to="/entrar" replace state={{ from: location.pathname }} />;
  }
  return children;
}

/** A tela do item do menu, ou o marcador de roteiro quando o modulo ainda nao a tem. */
function telaDoItem(item: NavItem): ReactElement {
  const Pagina = telasDoMenu[item.path];
  return Pagina ? (
    <Pagina />
  ) : (
    <ModulePlaceholder title={item.label} phase={item.phase} />
  );
}

/** Rota fora da moldura, com a sessao exigida e a pagina nao encontrada no fim. */
function rotaAvulsa(rota: RotaAvulsa): ReactElement {
  const element = rota.exigeSessao ? (
    <RequireAuth>{rota.element}</RequireAuth>
  ) : (
    rota.element
  );
  return (
    <Route key={rota.path} path={rota.path} element={element}>
      {rota.filhas
        ? [
            ...rota.filhas.map((filha) =>
              'index' in filha ? (
                <Route key="index" index element={filha.element} />
              ) : (
                <Route key={filha.path} path={filha.path} element={filha.element} />
              ),
            ),
            <Route key="*" path="*" element={<NotFoundPage />} />,
          ]
        : undefined}
    </Route>
  );
}

export function AppRouter() {
  // As rotas dos modulos nascem do mesmo mapa que monta o menu. Assim, menu e
  // roteamento nao divergem com o tempo. O item "/" e a rota inicial da moldura.
  const itensDoMenu = allNavItems();
  const conhecidas = [
    ...itensDoMenu.map((item) => item.path),
    ...rotasDaMoldura
      .filter((rota) => rota.conhecidaSemSessao !== false)
      .map((rota) => rota.path),
  ];
  const conhecida = (caminho: string) =>
    conhecidas.some((rota) => matchPath({ path: rota, end: true }, caminho) !== null);

  // A pagina nao encontrada de sempre, com ou sem sessao: o que a rota que pega o resto
  // mostraria para o mesmo endereco.
  const naoEncontrada = (
    <RequireAuth conhecida={conhecida}>
      <AppShell>
        <NotFoundPage />
      </AppShell>
    </RequireAuth>
  );

  return (
    <Routes>
      {rotasAvulsas.map(rotaAvulsa)}

      {/* Cadastro publico de corretores no endereco da empresa (ADR 0095), como
          /sudaseg. Toda rota fixa ganha desta: so o caminho de um segmento que nao e
          rota da aplicacao chega aqui, e a tela pergunta ao servidor se ele e um
          cadastro publico; se nao for, mostra a pagina nao encontrada de sempre. */}
      <Route
        path="/:enderecoPublico"
        element={<BrokerPublicSignupPage naoEncontrada={naoEncontrada} />}
      />

      <Route
        element={
          <RequireAuth conhecida={conhecida}>
            <AppShell />
          </RequireAuth>
        }
      >
        {itensDoMenu.map((item) =>
          item.path === '/' ? (
            <Route key={item.key} index element={telaDoItem(item)} />
          ) : (
            <Route key={item.key} path={item.path} element={telaDoItem(item)} />
          ),
        )}

        {rotasDaMoldura.map((rota) => (
          <Route key={rota.path} path={rota.path} element={rota.element} />
        ))}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
